import styled from '@emotion/styled'
import { ChevronLeft, ChevronRight, Logout } from '@mui/icons-material'
import {
  Avatar,
  Box,
  Divider as MuiDivider,
  IconButton,
  ListItemButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { spacing } from '@mui/system'
import React from 'react'
import { useNavigate } from 'react-router-dom'

import useAuth from '@/hooks/useAuth'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import useT from '@/i18n/useT'

const Footer = styled.div`
  background-color: ${props =>
    props.theme.sidebar.footer.background} !important;
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(4)};
  border-right: 1px solid rgba(0, 0, 0, 0.12);
  margin-top: auto;
  overflow: hidden;
`

const Divider = styled(MuiDivider)(spacing)

const UserButton = styled(ListItemButton)`
  padding: ${props => props.theme.spacing(2)};
  border-radius: ${props => props.theme.spacing(1)};
  margin-bottom: ${props => props.theme.spacing(1)};

  &:hover {
    background-color: ${props => props.theme.palette.action.hover};
  }
`

const LogoutButton = styled(ListItemButton)`
  padding: ${props => props.theme.spacing(2)};
  border-radius: ${props => props.theme.spacing(1)};
  margin-bottom: ${props => props.theme.spacing(1)};

  &:hover {
    background-color: ${props => props.theme.palette.action.hover};
  }
`

type SidebarFooterProps = {
  onCollapse?: () => void
  collapsed?: boolean
}

const SidebarFooter: React.FC<SidebarFooterProps> = ({
  onCollapse,
  collapsed = false,
}) => {
  const { signOut } = useAuth()
  const { user } = useLoggedInUser('cache-first')
  const navigate = useNavigate()
  const { t } = useT()

  const handleLogout = () => {
    signOut()
    navigate('/auth/sign-in')
  }

  const getInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    }
    return user?.email?.[0]?.toUpperCase() || '?'
  }

  const getUserRole = () => {
    const groups = user?.roles || []
    if (groups.length > 0) {
      return groups.map(g => g).join(', ')
    }
    return t('promo.sidebarFooter.role.user')
  }

  const userName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.email

  if (collapsed) {
    return (
      <Footer>
        <Divider my={2} />
        <Stack spacing={1} alignItems="center">
          <Tooltip title={userName || ''} placement="right">
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'primary.main',
                cursor: 'default',
              }}
            >
              {getInitials()}
            </Avatar>
          </Tooltip>

          <Tooltip title={t('promo.sidebarFooter.logout')} placement="right">
            <IconButton onClick={handleLogout} size="small">
              <Logout fontSize="small" />
            </IconButton>
          </Tooltip>

          {onCollapse && (
            <Tooltip
              title={t('promo.sidebarFooter.expandSidebar')}
              placement="right"
            >
              <IconButton onClick={onCollapse} size="small">
                <ChevronRight />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Footer>
    )
  }

  return (
    <Footer>
      <Divider my={2} />
      <Stack spacing={1}>
        <UserButton>
          <Stack direction="row" spacing={2} alignItems="center" width="100%">
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: 'primary.main',
              }}
            >
              {getInitials()}
            </Avatar>
            <Box flex={1} minWidth={0}>
              <Typography variant="body2" fontWeight="bold" noWrap>
                {userName}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {getUserRole()}
              </Typography>
            </Box>
          </Stack>
        </UserButton>

        <LogoutButton onClick={handleLogout}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Logout fontSize="small" />
            <Typography variant="body2">
              {t('promo.sidebarFooter.logout')}
            </Typography>
          </Stack>
        </LogoutButton>

        {onCollapse && (
          <Box display="flex" justifyContent="flex-end">
            <IconButton onClick={onCollapse} size="small">
              <ChevronLeft />
            </IconButton>
          </Box>
        )}
      </Stack>
    </Footer>
  )
}

export default SidebarFooter
